import { Avatar, Form } from 'antd';
import { useMemo, useRef, useState } from 'react';
import shopSelectors from 'services/shop/selectors';
import shopActions from 'services/shop/actions';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import AddOrUpdateProductModal, { IModalAddOrUpdateProductRef } from './Modals/AddOrUpdateProductModal';
import { IProductItemData } from 'services/shop/types/product';
import InputSearchSuffixIcon from 'components/InputSearchSuffixIcon';
import CheckboxV2 from 'components/CheckboxV2';
import Button from 'components/Button';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import { getMessageError, IResponseDataBody } from 'services/response';
import { toast } from 'react-toastify';
import { setLoadingPage } from 'services/UI/sagas';
import shopApis from 'services/shop/apis';
import { IDeleteProductsParams } from 'services/shop/types/categories';
import PopoverChangeColors from 'features/settingService/pages/SettingPage/components/ServicesSettingV2/ServicePart/PopoverChangeColors';
import Box from 'components/Box';
import { updateProduct } from 'features/settingService/services/apis';
import { IResponseDataBodyProduct } from 'features/settingService/services/types/product';
import ArrangeProductsModal from './Modals/ArrangeProductModal';
import ChangeColorButton from './ChangeColorButton';

interface Props {
  value?: string;
}

const ProductsByCategoryId = ({ value }: Props) => {
  const dispatch = useAppDispatch();
  const modal = useModalConfirmRef();
  const [searchText, setSearchText] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<IProductItemData[]>([]);
  const addOrUpdateProductModalRef = useRef<IModalAddOrUpdateProductRef>(null);
  const categories = shopSelectors.data.productCategories();
  const categorySelected = useMemo(() => categories.find((category) => category.id === value), [categories, value]);
  const handleAddProduct = () => {
    addOrUpdateProductModalRef.current?.open({ catId: value || '' });
  };
  const handleClickProduct = (product: IProductItemData) => {
    addOrUpdateProductModalRef.current?.open(product);
  };

  const filteredProducts = useMemo(() => {
    return categorySelected?.products.filter((product) => product.productName.toLowerCase().includes(searchText.toLowerCase())) || [];
  }, [categorySelected?.products, searchText]);

  const handleDeleteProducts = async () => {
    try {
      setLoadingPage(true);
      const body: IDeleteProductsParams = {
        categoryId: value || '',
        ids: selectedProducts.map((p) => p.id),
      };
      const res: IResponseDataBody<boolean> = await shopApis.deleteProducts(body);
      if (res.data.data) {
        toast.success('Delete products successfully');
        setSelectedProducts([]);
        dispatch(shopActions.deleteProducts(body));
      } else {
        toast.error('Delete products failed');
      }
    } catch (error) {
      const msg = getMessageError(error);
      toast.error(msg || 'Delete products failed');
    } finally {
      setLoadingPage(false);
    }
  };

  const handleOpenConfirmDeleteProducts = () => {
    modal.current?.open('Are you sure you want to delete the selected products?', handleDeleteProducts);
  };

  const handleCheckboxClick = (e: React.MouseEvent<HTMLDivElement>, product: IProductItemData) => {
    e.stopPropagation();
    if (selectedProducts.some((p) => p.id === product.id)) {
      setSelectedProducts((prev) => prev.filter((p) => p.id !== product.id));
    } else {
      setSelectedProducts((prev) => [...prev, product]);
    }
  };

  const handleColorChange = async (color: string, product: IProductItemData) => {
    try {
      const res: IResponseDataBody<IResponseDataBodyProduct> = await updateProduct({
        ...product,
        supplierId: product.supplierId || undefined,
        capacity: product.capacity || undefined,
        colorCode: product.colorCode || undefined,
        expiredDate: product.expiredDate || undefined,
        description: product.description || undefined,
        colorBackground: color,
      });

      if(res?.data?.data) {
        toast.success('Update color successfully');
        dispatch(shopActions.get.updateProduct(res?.data?.data));
      }
    } catch (error) {
        toast.error(getMessageError(error) || 'Update color failed');
    }
  };

  const handleOpenPopover = (e: React.MouseEvent) => {
    e.stopPropagation();
    document.body.click();
  };

  return (
    <Wrapper>
      <SelectedBox>
        <InputSearchSuffixIcon value={searchText} onChange={setSearchText} placeholder='Search by Name' />
        <ChangeColorButton catId={categorySelected?.id || ''} />
        <ArrangeProductsModal data={categorySelected?.products || []} />
        <Button ntype='LIGHT_PRIMARY' icon='deleteService' disabled={selectedProducts.length === 0} onClick={handleOpenConfirmDeleteProducts}>Delete Products</Button>
      </SelectedBox>
      <ProductsContainer>
        <AddProductItem onClick={handleAddProduct}>
          <IcAdd />
          <div className='add-product-item-text'>Add Product</div>
        </AddProductItem>

        {filteredProducts?.map((product) => {
          return (
            <Item key={product.id} onClick={() => handleClickProduct(product)}>
              <Box width='100%' display='flex' justifyContent='space-between'>
                <CheckboxV2 onClick={(e: React.MouseEvent<HTMLDivElement>) => handleCheckboxClick(e, product)} />
                <div onClick={(e) => handleOpenPopover(e)} 
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <PopoverChangeColors value={product.colorBackground || ''} onChange={(color) => handleColorChange(color, product)} />
                </div>
              </Box>
              <div className='wrapper'>
                {product.urlImage ?
                  <div className='image'>
                    <Avatar src={product.urlImage} size={82} shape='square' />
                  </div>
                  : <DefaultImageWrapper><DefaultImage /></DefaultImageWrapper>}
                <div className='info'>
                  <div className='product-name'>{product.productName}</div>
                  <div className='product-another-info-container'>
                    <div className='product-another-info'>Unit:{product.unit}</div>
                    <div className='product-another-info align-right'>{formatCurrency(product.priceSell)}</div>
                  </div>
                </div>
              </div>
            </Item>
          );
        })}
      </ProductsContainer>
      <AddOrUpdateProductModal ref={addOrUpdateProductModalRef} />
      <ModalConfirm ref={modal} />
    </Wrapper>
  );
};

export default ProductsByCategoryId;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 68px);
  overflow-y: auto;
`;

const SelectedBox = styled.div`
  position: sticky;
  top: 0;
  padding: 16px;
  z-index: 100;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid #e9edf2;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ProductsContainer = styled.div`
  padding: 24px 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(263px, 1fr));
  gap: 16px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(auto-fill, minmax(263px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(263px, 1fr));
  }
`;

const AddProductItem = styled.div`
  border-radius: 8px;
  border: 1px dashed #232f3e;
  background: #e5e6eb;

  display: flex;
  height: 100px;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
  .add-product-item-text {
    color: #1d2129;
    text-align: center;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 16px 8px 8px;
  align-items: flex-start;
  gap: 12px;

  border-radius: 8px;
  border: 1px solid #e9edf2;
  background: #ffffff;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

  .wrapper {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex: 1 0 0;
    align-self: stretch;
  }
  .image {
    border-radius: 4px;
    background: #d9d9d9;
  }

  .info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0px;
    flex: 1 0 0;
    .product-name {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      align-self: stretch;
      overflow: hidden;
      color: #1d2129;
      text-overflow: ellipsis;
      font-family: Open Sans;
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
    .product-another-info {
      color: #505050;
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
    .product-another-info-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0px;
      align-self: stretch;
    }
    .product-another-info.align-right {
      text-align: right;
    }
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const DefaultImageWrapper = styled.div`
  width: 82px;
  height: 82px;
  border-radius: 4px;
  background: #d9d9d9;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const IcAdd = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='41' height='41' viewBox='0 0 41 41' fill='none'>
    <path
      d='M20.7 6.64648C21.9168 6.64669 22.9031 7.63372 22.9031 8.85059V18.5459H32.5984C33.8153 18.5459 34.8023 19.5322 34.8026 20.749C34.8026 21.966 33.8154 22.9531 32.5984 22.9531H22.9031V32.6484C22.9031 33.8653 21.9168 34.8523 20.7 34.8525C19.483 34.8525 18.4959 33.8654 18.4959 32.6484V22.9531H8.8006C7.5836 22.9531 6.5965 21.966 6.5965 20.749C6.59672 19.5322 7.58374 18.5459 8.8006 18.5459H18.4959V8.85059C18.4959 7.63359 19.483 6.64648 20.7 6.64648Z'
      fill='#232F3E'
    />
  </svg>
);

const DefaultImage = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='57' height='57' viewBox='0 0 57 57' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M12.626 10.0833C11.3373 10.0833 10.2926 11.128 10.2926 12.4166V45.0833C10.2926 46.0838 10.9224 46.9373 11.8072 47.2689L36.6427 22.4334C37.5539 21.5222 39.0313 21.5222 39.9426 22.4334L47.626 30.1168V12.4166C47.626 11.128 46.5813 10.0833 45.2926 10.0833H12.626ZM52.2926 35.7477V12.4166C52.2926 8.55063 49.1586 5.41663 45.2926 5.41663H12.626C8.75998 5.41663 5.62598 8.55063 5.62598 12.4166V45.0833C5.62598 48.9493 8.75998 52.0833 12.626 52.0833H45.2926C49.1586 52.0833 52.2926 48.9493 52.2926 45.0833V35.7522C52.2926 35.7507 52.2926 35.7492 52.2926 35.7477ZM47.626 36.7165L38.2926 27.3831L18.2591 47.4166H45.2926C46.5813 47.4166 47.626 46.372 47.626 45.0833V36.7165ZM20.7926 19.4166C20.1483 19.4166 19.626 19.939 19.626 20.5833C19.626 21.2276 20.1483 21.75 20.7926 21.75C21.437 21.75 21.9593 21.2276 21.9593 20.5833C21.9593 19.939 21.437 19.4166 20.7926 19.4166ZM14.9593 20.5833C14.9593 17.3616 17.571 14.75 20.7926 14.75C24.0143 14.75 26.626 17.3616 26.626 20.5833C26.626 23.805 24.0143 26.4166 20.7926 26.4166C17.571 26.4166 14.9593 23.805 14.9593 20.5833Z'
      fill='#777D7C'
    />
  </svg>
);